import type { Components, Theme } from '@mui/material'
import { colors, layout } from './tokens'

/**
 * MUI component overrides — this is what turns MUI from "Material Design" into
 * the True Forge industrial-luxury system (per CLAUDE.md: never look like a UI kit).
 */
export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      html: { scrollBehavior: 'smooth', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
      body: { backgroundColor: colors.bg, color: colors.white, overflowX: 'hidden' },
      '::selection': { background: colors.gold, color: colors.bg },
      a: { color: 'inherit', textDecoration: 'none' },
      img: { display: 'block', maxWidth: '100%' },
      '*:focus-visible': { outline: `2px solid ${colors.gold}`, outlineOffset: '2px' },
    },
  },
  MuiButtonBase: { defaultProps: { disableRipple: true } },
  MuiButton: {
    defaultProps: { disableElevation: true, variant: 'outlined' },
    styleOverrides: {
      root: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: layout.radius.button,
        minHeight: 56,
        paddingInline: 28,
        transition: 'background-color 300ms ease, color 300ms ease, border-color 300ms ease, transform 250ms ease, box-shadow 300ms ease',
        '&:active': { transform: 'translateY(0)' },
      },
      sizeSmall: { minHeight: 44, paddingInline: 20 },
      // Shine sweep on hover — shared by the two solid variants.
      outlined: {
        borderColor: colors.gold,
        color: colors.white,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-130%',
          width: '55%',
          height: '100%',
          transform: 'skewX(-20deg)',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
          transition: 'left 600ms ease',
          pointerEvents: 'none',
        },
        '&:hover': {
          backgroundColor: colors.gold,
          color: colors.bg,
          borderColor: colors.gold,
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 30px -10px rgba(212,175,55,0.7)',
        },
        '&:hover::before': { left: '130%' },
      },
      contained: {
        backgroundColor: colors.gold,
        color: colors.bg,
        animation: 'tfGlow 2.8s ease-in-out infinite',
        '@keyframes tfGlow': {
          '0%, 100%': { filter: 'drop-shadow(0 0 3px rgba(212,175,55,0.35))' },
          '50%': { filter: 'drop-shadow(0 0 13px rgba(212,175,55,0.7))' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-130%',
          width: '55%',
          height: '100%',
          transform: 'skewX(-20deg)',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
          transition: 'left 600ms ease',
          pointerEvents: 'none',
        },
        '&:hover': {
          backgroundColor: colors.goldHover,
          transform: 'translateY(-2px)',
          boxShadow: '0 14px 34px -8px rgba(212,175,55,0.85)',
        },
        '&:hover::before': { left: '130%' },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      },
      text: {
        color: colors.white,
        paddingInline: 8,
        minHeight: 'auto',
        overflow: 'visible',
        '&:hover': { color: colors.gold, backgroundColor: 'transparent' },
      },
    },
  },
  MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: layout.radius.card,
        backgroundImage: 'none',
        transition: 'border-color 300ms ease, transform 300ms ease',
      },
    },
  },
  MuiContainer: { defaultProps: { maxWidth: false } },
  MuiLink: { defaultProps: { underline: 'none' }, styleOverrides: { root: { transition: 'color 200ms ease' } } },
  MuiDivider: { styleOverrides: { root: { borderColor: colors.border } } },
  MuiIconButton: { styleOverrides: { root: { borderRadius: layout.radius.button } } },
  MuiTextField: { defaultProps: { fullWidth: true, variant: 'outlined' } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: layout.radius.button,
        backgroundColor: colors.surface,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderStrong },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold, borderWidth: 1 },
      },
      input: { padding: '15px 18px' },
    },
  },
  MuiInputLabel: {
    styleOverrides: { root: { color: colors.grey, '&.Mui-focused': { color: colors.gold } } },
  },
  MuiChip: {
    styleOverrides: {
      outlined: { borderColor: colors.borderStrong, color: colors.white },
    },
  },
}
